import TurndownService from 'turndown'
import { pdfEmbedMarkdown, replacePdfMarkdown } from '../utils/pdfEmbed.js'
import { escapeHtmlText, isSafeHref } from '../utils/security.js'

// Escaping and the URL-scheme policy are app-wide concerns and live in
// utils/security.js — a second copy here is how the two markdown converters
// drifted apart in the first place.
const escapeMarkdownText = escapeHtmlText
const isSafeMarkdownHref = (href) => isSafeHref(href, { allowMailto: true })

// A markdown link/image destination. A lazy `(.+?)` stops at the FIRST `)`, so
// any URL carrying parentheses (Wikipedia is full of them —
// `/wiki/Variable_(math)`) produced a truncated href plus a stray `)` in the
// text. Allow ONE level of balanced parens, the CommonMark rule. Newlines stay
// excluded so an unclosed `(` can't swallow the rest of the document.
const LINK_DESTINATION = '(?:[^()\\n]|\\([^()\\n]*\\))+'

// `- item` / `* item` / `1. item` / `1) item`. Turndown pads its markers
// (`-   item`, `1.  item`), so the separator is `\s+`, not a single space.
const LIST_ITEM = /^(?:([*-])|\d{1,9}[.)])\s+(.*)$/

const listTypeOf = (line) => {
  const match = line.match(LIST_ITEM)
  if (!match) return null
  return match[1] ? 'ul' : 'ol'
}

// The inline (span-level) half of the markdown renderer, split out so it can
// also be applied to content that is ALREADY HTML — see inlineMarkdownInHtml.
// Kept as the single definition of these rules on purpose: a second copy is how
// the two markdown converters drifted apart before.
const applyInlineMarkdown = (html) =>
  html
    // Images — MUST run before the link rule below, which would otherwise match
    // the [..](..) portion of ![alt](url) and emit a stray-"!" anchor. Pasted
    // note images are stored as markdown (Turndown turns <img> into ![](url) on
    // save), so this is what renders them back as an <img>. Only emit an image
    // for safe schemes; drop it otherwise. alt/url were already HTML-escaped by
    // escapeMarkdownText during line processing, and render sinks sanitize the
    // output, so attribute breakout isn't possible here.
    .replace(new RegExp(`!\\[([^\\]]*)\\]\\((${LINK_DESTINATION})\\)`, 'g'), (match, alt, url) =>
      isSafeMarkdownHref(url) ? `<img src="${url}" alt="${alt}">` : ''
    )
    // Bold / italic. The delimiter must hug its content (CommonMark's flanking
    // rule): without that, prose like `3 * 4 * 5` matches and renders as
    // `3 <em> 4 </em> 5`. Requiring \S on both ends leaves arithmetic, bullets
    // and stray asterisks alone while `**bold**` still matches.
    .replace(/\*\*(\S(?:[\s\S]*?\S)?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(\S(?:[\s\S]*?\S)?)\*/g, '<em>$1</em>')
    // Links — only emit an anchor for safe schemes; otherwise keep the label text.
    .replace(new RegExp(`\\[(.+?)\\]\\((${LINK_DESTINATION})\\)`, 'g'), (match, text, url) =>
      isSafeMarkdownHref(url) ? `<a href="${url}">${text}</a>` : text
    )
    // Code
    .replace(/`(.+?)`/g, '<code>$1</code>')

// Splits on tags so the replacements only ever touch text, never a tag's own
// attributes. A tag does NOT end at the first `>`: `<img alt="a > b">` carries
// one inside a quoted value, and a naive /(<[^>]*>)/ would cut the tag in half
// and then rewrite the remainder as if it were prose. Consume quoted runs whole.
const TAG_OR_TEXT = /(<[a-zA-Z/!][^>"']*(?:(?:"[^"]*"|'[^']*')[^>"']*)*>)/

// Inside these, the text is meant to be read literally — markdown must not run.
// `code`/`pre` would gain stray <em> where a code sample uses `*`, and nesting a
// generated <code>/<a> inside an existing one produces invalid markup.
const VERBATIM_ELEMENTS = /^(pre|code|a|script|style|textarea)$/i

const tagNameOf = (tag) => {
  const match = /^<\/?([a-zA-Z][^\s/>]*)/.exec(tag)
  return match ? match[1] : ''
}

/**
 * Apply inline markdown to a string that already contains HTML.
 *
 * Stored knowledge content is routinely MIXED: the editor writes an `<img>` tag
 * and leaves the prose around it as markdown. `markdownToHtml` cannot be used
 * on that — it HTML-escapes every line, so the tags would surface as visible
 * `&lt;img&gt;`. Feeding such content through the "looks like HTML, pass it
 * through" branch instead left `**bold**` on screen for the reader.
 *
 * Text inside a verbatim element (`<pre>`, `<code>`, `<a>`, …) is left alone.
 */
export const inlineMarkdownInHtml = (html) => {
  if (!html || typeof html !== 'string') return ''
  // Depth rather than a boolean: `<pre><code>…</code></pre>` closes twice, and a
  // flag would resume formatting at the inner `</code>` while still inside <pre>.
  let verbatimDepth = 0
  return html
    .split(TAG_OR_TEXT)
    .map(part => {
      if (!part.startsWith('<')) {
        return verbatimDepth > 0 ? part : applyInlineMarkdown(part)
      }
      const name = tagNameOf(part)
      if (VERBATIM_ELEMENTS.test(name) && !/\/>$/.test(part)) {
        if (part.startsWith('</')) verbatimDepth = Math.max(0, verbatimDepth - 1)
        else verbatimDepth += 1
      }
      return part
    })
    .join('')
}

/**
 * Composable for converting between HTML and Markdown
 * Preserves HTML for features not supported in Markdown (like background colors)
 */
export function useMarkdownConverter() {
  // Configure Turndown to preserve certain HTML elements
  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
    bulletListMarker: '-'
  })

  // PDF embeds must survive the HTML→markdown leg of the round-trip: bookmark
  // notes are rendered to HTML for editing (markdownToHtml) and converted back
  // on save. Without this rule Turndown drops the unknown <iframe> and reduces
  // the whole embed to `[filename.pdf](url)`, losing the marker and its size.
  // Registered first so it wins over the generic class/style rules below.
  turndownService.addRule('pdfEmbed', {
    filter: function (node) {
      return node.nodeName === 'DIV' && (
        node.hasAttribute('data-pdf-embed') ||
        (node.classList && node.classList.contains('pdf-embed-wrapper'))
      )
    },
    replacement: function (content, node) {
      // Accept both the current <iframe> markup and the legacy <object> markup.
      const frame = node.querySelector('iframe, object')
      const link = node.querySelector('a')
      const src = node.getAttribute('data-src') ||
        frame?.getAttribute('src') ||
        frame?.getAttribute('data') ||
        link?.getAttribute('href') || ''
      if (!src) return content
      const width = node.getAttribute('data-width') || frame?.getAttribute('width')
      const height = node.getAttribute('data-height') || frame?.getAttribute('height')
      return `\n\n${pdfEmbedMarkdown(src, width, height)}\n\n`
    }
  })

  // Add rule to preserve elements with background colors or other non-markdown styling
  turndownService.addRule('preserveStyledElements', {
    filter: function (node) {
      // Preserve elements with background color
      if (node.style && node.style.backgroundColor) {
        return true
      }
      // Preserve elements with custom classes that might indicate special styling
      if (node.classList && node.classList.length > 0) {
        // Check if any class suggests special formatting
        const specialClasses = Array.from(node.classList).some(cls => 
          cls.includes('highlight') || 
          cls.includes('color') || 
          cls.includes('bg-')
        )
        if (specialClasses) return true
      }
      return false
    },
    replacement: function (content, node) {
      // Return the original HTML for styled elements
      return node.outerHTML
    }
  })

  // Add rule to preserve span elements with inline styles
  turndownService.addRule('preserveSpanWithStyle', {
    filter: function (node) {
      return (
        node.nodeName === 'SPAN' &&
        node.hasAttribute('style') &&
        (node.style.backgroundColor || 
         node.style.color ||
         node.style.fontSize)
      )
    },
    replacement: function (content, node) {
      return node.outerHTML
    }
  })

  /**
   * Convert HTML to Markdown
   * Preserves HTML for features not supported in Markdown
   * @param {string} html - HTML content from Quill editor
   * @returns {string} - Markdown with preserved HTML for unsupported features
   */
  const htmlToMarkdown = (html) => {
    if (!html || typeof html !== 'string') {
      return ''
    }
    
    // If the content is already plain text, return it as-is
    if (!html.includes('<')) {
      return html
    }

    try {
      return turndownService.turndown(html)
    } catch (error) {
      console.error('Error converting HTML to Markdown:', error)
      // Return original HTML as fallback
      return html
    }
  }

  /**
   * Convert Markdown (with HTML) back to HTML for the editor
   * @param {string} markdown - Markdown content with potential HTML
   * @returns {string} - HTML content for Quill editor
   */
  const markdownToHtml = (markdown) => {
    if (!markdown || typeof markdown !== 'string') {
      return ''
    }

    // If content is already HTML (contains tags), return as-is
    // Quill can handle mixed HTML/Markdown content
    if (markdown.includes('<p>') || markdown.includes('<div>')) {
      return markdown
    }

    // Simple markdown to HTML conversion for common patterns
    let html = markdown

    // PDF embeds (`[PDF](url){width=W height=H}`) are pulled out first and
    // parked behind a placeholder: their HTML must survive the per-line escaping
    // below, and without this the generic link rule would render them as
    // `<a>PDF</a>{width=800 height=600}` — leaking the raw marker into every
    // read-only surface (card previews, public view, link notes). The token uses
    // NUL so no escaping/inline rule can touch it. See utils/pdfEmbed.js.
    const pdfEmbeds = []
    html = replacePdfMarkdown(html, (embedHtml) => {
      pdfEmbeds.push(embedHtml)
      return `\u0000PDFEMBED${pdfEmbeds.length - 1}\u0000`
    })

    // Split by lines to handle different types of content
    const lines = html.split('\n')
    const processedLines = []
    let inCodeBlock = false
    let listTag = null

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i]
      
      // Handle code blocks
      if (line.trim().startsWith('```')) {
        inCodeBlock = !inCodeBlock
        if (inCodeBlock) {
          processedLines.push('<pre><code>')
        } else {
          processedLines.push('</code></pre>')
        }
        continue
      }
      
      if (inCodeBlock) {
        // Escape so `<script>` etc. inside a code block renders literally.
        processedLines.push(escapeMarkdownText(line))
        continue
      }

      // Lists — unordered and ordered. Ordered lists used to fall through to the
      // paragraph branch, so a pasted numbered list rendered as plain lines.
      const item = line.match(LIST_ITEM)
      if (item) {
        const tag = item[1] ? 'ul' : 'ol'
        if (listTag && listTag !== tag) {
          processedLines.push(`</${listTag}>`)
          listTag = null
        }
        if (!listTag) {
          processedLines.push(`<${tag}>`)
          listTag = tag
        }
        processedLines.push(`<li>${escapeMarkdownText(item[2])}</li>`)
        continue
      }

      // Empty lines
      if (line.trim() === '') {
        // A blank line BETWEEN items of the same list is "loose list" spacing —
        // Turndown emits it and so did the editor's converter, so most stored
        // lists look like this. Swallow it, or every item becomes its own list.
        const nextContentLine = lines.slice(i + 1).find(l => l.trim() !== '')
        if (listTag && nextContentLine && listTypeOf(nextContentLine) === listTag) {
          continue
        }
        if (listTag) {
          processedLines.push(`</${listTag}>`)
          listTag = null
        }
        processedLines.push('<br>')
        continue
      }

      // Any other content ends an open list (headings included — closing it
      // only in the paragraph branch left the heading nested inside the <ul>).
      if (listTag) {
        processedLines.push(`</${listTag}>`)
        listTag = null
      }

      // Headers
      if (line.match(/^#{1,6} /)) {
        const level = line.match(/^#{1,6}/)[0].length
        const text = line.replace(/^#{1,6} /, '')
        processedLines.push(`<h${level}>${escapeMarkdownText(text)}</h${level}>`)
        continue
      }

      // Regular paragraphs with inline formatting (text escaped to prevent markup injection)
      processedLines.push(`<p>${escapeMarkdownText(line)}</p>`)
    }

    // Close any open lists
    if (listTag) {
      processedLines.push(`</${listTag}>`)
    }
    
    html = processedLines.join('\n')
    
    // Inline formatting
    html = applyInlineMarkdown(html)

    // Put the PDF embeds back. The embed is a block-level <div>, so a paragraph
    // holding one is split around it — nesting a <div> inside a <p> is invalid
    // HTML and the browser would hoist it out, wrecking the surrounding text.
    if (pdfEmbeds.length > 0) {
      const embedFor = (i) => pdfEmbeds[Number(i)] ?? ''
      html = html
        .replace(/<p>([\s\S]*?)<\/p>/g, (match, inner) => {
          if (!inner.includes('\u0000PDFEMBED')) return match
          return inner
            .split(/(\u0000PDFEMBED\d+\u0000)/)
            .map(part => {
              const token = /^\u0000PDFEMBED(\d+)\u0000$/.exec(part)
              if (token) return embedFor(token[1])
              return part.trim() ? `<p>${part}</p>` : ''
            })
            .join('')
        })
        // Placeholders outside a paragraph (heading, list item, bare line).
        .replace(/\u0000PDFEMBED(\d+)\u0000/g, (match, i) => embedFor(i))
    }

    return html
  }

  return {
    htmlToMarkdown,
    markdownToHtml
  }
}
